/* Mark's Outdoors — shared vanilla FX.
   Ports of the React app's use-reveal / use-loop hooks, CascadeText, and the
   sketch-ink SVG system (HandUnderline, CircleHover). No dependencies.
   All motion respects prefers-reduced-motion. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Reveal (use-reveal.ts) ----------
     <el data-reveal [data-reveal-threshold="0.15"]> gains .is-shown once it
     enters the viewport (one-shot). CSS decides what .is-shown means. */
  function initReveals(root) {
    var els = (root || document).querySelectorAll('[data-reveal]:not(.is-shown)');
    if (!els.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-shown'); });
      return;
    }
    els.forEach(function (el) {
      var threshold = parseFloat(el.getAttribute('data-reveal-threshold') || '0.15');
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              el.classList.add('is-shown');
              io.disconnect();
            }
          });
        },
        { threshold: threshold }
      );
      io.observe(el);
    });
  }

  /* ---------- Loop (use-loop.ts) ----------
     <el data-loop="3000"> gets its .mo-loop-tick class retoggled every N ms,
     restarting any CSS animation keyed to it. Skipped under reduced motion. */
  function initLoops(root) {
    (root || document).querySelectorAll('[data-loop]').forEach(function (el) {
      if (el.__moLoop || reducedMotion.matches) return;
      var delay = parseInt(el.getAttribute('data-loop'), 10) || 3000;
      el.__moLoop = window.setInterval(function () {
        el.classList.remove('mo-loop-tick');
        // Force reflow so the animation restarts.
        void el.offsetWidth;
        el.classList.add('mo-loop-tick');
      }, delay);
    });
  }

  /* ---------- CascadeText ----------
     <el data-cascade> — characters roll up on hover (nav links). Splits text
     into per-char spans with staggered transitions. Skipped under reduced
     motion (plain text stays). */
  function initCascade(root) {
    (root || document).querySelectorAll('[data-cascade]').forEach(function (el) {
      if (el.__moCascade) return;
      el.__moCascade = true;
      if (reducedMotion.matches) return;
      var text = el.textContent;
      var chars;
      if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        var seg = new Intl.Segmenter('en', { granularity: 'grapheme' });
        chars = Array.from(seg.segment(text), function (s) { return s.segment; });
      } else {
        chars = Array.from(text);
      }
      el.setAttribute('aria-label', text);
      el.textContent = '';
      var wrap = document.createElement('span');
      wrap.className = 'mo-cascade';
      wrap.setAttribute('aria-hidden', 'true');
      chars.forEach(function (ch, i) {
        var s = document.createElement('span');
        s.className = 'mo-cascade__char';
        s.style.transitionDelay = i * 20 + 'ms';
        s.textContent = ch === ' ' ? ' ' : ch;
        wrap.appendChild(s);
      });
      el.appendChild(wrap);
      var hoverTarget = el.closest('a, button') || el;
      hoverTarget.addEventListener('mouseenter', function () { el.classList.add('is-hovered'); });
      hoverTarget.addEventListener('mouseleave', function () { el.classList.remove('is-hovered'); });
    });
  }

  /* ---------- Sketch ink: HandUnderline ----------
     <span data-hand-underline data-ink="amber|brick"> gets the jittered
     underline SVG that draws on when revealed in the viewport. */
  var INK = { brick: '#B01C21', amber: '#F5B914' };

  function initHandUnderlines(root) {
    (root || document).querySelectorAll('[data-hand-underline]').forEach(function (el) {
      if (el.__moUnderline) return;
      el.__moUnderline = true;
      var color = INK[el.getAttribute('data-ink')] || INK.brick;
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 200 14');
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.setAttribute('aria-hidden', 'true');
      svg.classList.add('mo-hand-underline');
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M 2,8 C 28,3 58,11 92,6 C 130,2 160,12 198,5');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', color);
      path.setAttribute('stroke-width', '2.6');
      path.setAttribute('stroke-linecap', 'round');
      path.style.strokeDasharray = '240';
      path.style.strokeDashoffset = reducedMotion.matches ? '0' : '240';
      path.style.transition = 'stroke-dashoffset 1500ms cubic-bezier(0.22,1,0.36,1)';
      svg.appendChild(path);
      el.classList.add('mo-hand-underline-host');
      el.appendChild(svg);
      if (reducedMotion.matches || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              path.style.strokeDashoffset = '0';
              io.disconnect();
            }
          });
        },
        { threshold: 0.4 }
      );
      io.observe(el);
    });
  }

  /* ---------- Sketch ink: CircleHover ----------
     <span data-circle-hover> wrapping a CTA — on hover/focus an amber
     hand-drawn marker loops around it (deterministic scribble, port of
     sketch-ink.tsx buildScribble). */
  function smoothPath(pts) {
    if (pts.length < 2) return '';
    var f = function (n) { return n.toFixed(2); };
    var d = 'M ' + f(pts[0][0]) + ',' + f(pts[0][1]);
    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[i - 1] || pts[i];
      var p1 = pts[i];
      var p2 = pts[i + 1];
      var p3 = pts[i + 2] || p2;
      d += ' C ' + f(p1[0] + (p2[0] - p0[0]) / 6) + ',' + f(p1[1] + (p2[1] - p0[1]) / 6) +
        ' ' + f(p2[0] - (p3[0] - p1[0]) / 6) + ',' + f(p2[1] - (p3[1] - p1[1]) / 6) +
        ' ' + f(p2[0]) + ',' + f(p2[1]);
    }
    return d;
  }

  function buildScribble(w, h, loops) {
    var cx = w / 2, cy = h / 2;
    var baseRx = w / 2 - 3, baseRy = h / 2 - 3;
    var seed = (Math.round(w) * 73 + Math.round(h) * 31 + loops * 17) || 1;
    var rand = function () {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };
    var perLoop = 44;
    var steps = loops * perLoop;
    var startA = -Math.PI / 2 - 0.4;
    var span = loops * Math.PI * 2 + 0.6;
    var driftX = (rand() - 0.5) * 7;
    var driftY = (rand() - 0.5) * 7;
    var phase = rand() * Math.PI * 2;
    var pts = [];
    for (var i = 0; i <= steps; i++) {
      var t = i / steps;
      var a = startA + t * span;
      var grow = 0.82 + 0.22 * t;
      var wob = 1 + 0.05 * Math.sin(a * 3 + phase);
      var rx = baseRx * grow * wob + (rand() - 0.5) * 2.4;
      var ry = baseRy * grow * wob + (rand() - 0.5) * 2.4;
      pts.push([cx + driftX * t + rx * Math.cos(a), cy + driftY * t + ry * Math.sin(a)]);
    }
    return smoothPath(pts);
  }

  function initCircleHovers(root) {
    (root || document).querySelectorAll('[data-circle-hover]').forEach(function (host) {
      if (host.__moCircle) return;
      host.__moCircle = true;
      if (reducedMotion.matches) return;
      var loops = parseInt(host.getAttribute('data-circle-loops'), 10) || 3;
      var pad = 9;
      var svg = null, path = null, len = 1;
      function build() {
        var r = host.getBoundingClientRect();
        if (!r.width) return;
        var W = Math.round(r.width) + pad * 2;
        var H = Math.round(r.height) + pad * 2;
        if (!svg) {
          svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('aria-hidden', 'true');
          svg.classList.add('mo-circle-hover-svg');
          path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('fill', 'none');
          path.setAttribute('stroke', INK.amber);
          path.setAttribute('stroke-width', '2.4');
          path.setAttribute('stroke-linecap', 'round');
          path.setAttribute('stroke-linejoin', 'round');
          svg.appendChild(path);
          host.appendChild(svg);
        }
        svg.setAttribute('width', W);
        svg.setAttribute('height', H);
        svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
        svg.style.left = -pad + 'px';
        svg.style.top = -pad + 'px';
        path.setAttribute('d', buildScribble(W, H, loops));
        len = path.getTotalLength();
        path.style.strokeDasharray = len;
        path.style.strokeDashoffset = len;
        var drawMs = Math.min(1500, 300 * loops + 240);
        path.style.transition = 'stroke-dashoffset ' + drawMs + 'ms cubic-bezier(0.22,1,0.36,1)';
      }
      if ('ResizeObserver' in window) {
        new ResizeObserver(build).observe(host);
      } else {
        build();
      }
      function on() {
        if (!svg) return;
        svg.style.opacity = '1';
        path.style.strokeDashoffset = '0';
      }
      function off() {
        if (!svg) return;
        svg.style.opacity = '0';
        path.style.strokeDashoffset = len;
      }
      host.addEventListener('mouseenter', on);
      host.addEventListener('mouseleave', off);
      host.addEventListener('focusin', on);
      host.addEventListener('focusout', off);
    });
  }

  /* ---------- FAQ accordions ----------
     <details data-faq-anim> gets a slow, settled open/close (height tween +
     answer fade-in) instead of the native snap. Exclusivity is re-implemented
     here (the native `name` attribute snaps siblings shut with no animation,
     so we strip it and animate the sibling closed ourselves). No-JS falls
     back to the native name-grouped accordion. */
  function initFaqAccordions(root) {
    var OPEN_MS = 500;
    var CLOSE_MS = 400;
    var EASE = 'cubic-bezier(0.33, 1, 0.68, 1)';

    function contentOf(el) {
      var summary = el.querySelector('summary');
      return summary ? summary.nextElementSibling : null;
    }

    function expand(el) {
      if (reducedMotion.matches || !el.animate) { el.open = true; return; }
      el.__moFaqBusy = true;
      el.style.overflow = 'hidden';
      var startH = el.offsetHeight;
      el.open = true;
      var endH = el.offsetHeight;
      var content = contentOf(el);
      if (content && content.animate) {
        content.animate(
          { opacity: [0, 1], transform: ['translateY(-6px)', 'translateY(0)'] },
          { duration: OPEN_MS - 100, delay: 140, easing: 'ease-out', fill: 'backwards' }
        );
      }
      var anim = el.animate({ height: [startH + 'px', endH + 'px'] }, { duration: OPEN_MS, easing: EASE });
      anim.onfinish = anim.oncancel = function () {
        el.style.overflow = '';
        el.__moFaqBusy = false;
      };
    }

    function collapse(el) {
      if (reducedMotion.matches || !el.animate) { el.open = false; return; }
      el.__moFaqBusy = true;
      el.style.overflow = 'hidden';
      var startH = el.offsetHeight;
      el.open = false;
      var endH = el.offsetHeight;
      el.open = true;
      var content = contentOf(el);
      if (content && content.animate) {
        content.animate({ opacity: [1, 0] }, { duration: 180, easing: 'ease-out', fill: 'forwards' });
      }
      var anim = el.animate({ height: [startH + 'px', endH + 'px'] }, { duration: CLOSE_MS, easing: EASE });
      anim.onfinish = anim.oncancel = function () {
        el.open = false;
        el.style.overflow = '';
        el.__moFaqBusy = false;
        if (content) content.getAnimations().forEach(function (a) { a.cancel(); });
      };
    }

    (root || document).querySelectorAll('details[data-faq-anim]').forEach(function (el) {
      if (el.__moFaq) return;
      el.__moFaq = true;
      var summary = el.querySelector('summary');
      if (!summary) return;
      var group = el.getAttribute('name');
      if (group) {
        el.setAttribute('data-faq-group', group);
        el.removeAttribute('name');
      }
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (el.__moFaqBusy) return;
        if (el.open) {
          collapse(el);
        } else {
          if (group) {
            document.querySelectorAll('details[data-faq-group="' + group + '"][open]').forEach(function (other) {
              if (other !== el && !other.__moFaqBusy) collapse(other);
            });
          }
          expand(el);
        }
      });
    });
  }

  function initAll(root) {
    initReveals(root);
    initLoops(root);
    initCascade(root);
    initHandUnderlines(root);
    initCircleHovers(root);
    initFaqAccordions(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  // Re-init when the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.MoFX = { init: initAll };
})();
